import api from '../../../services/ApiService';
import type { PassportCard, CardHub } from '../../../services/api/passportCards';
export type View = 'binder' | 'stacks' | 'list' | 'explore';
export type Defaults = { view: View; group: string; sort: string };
export type PersonalPage = { id: string; title: string; keys: string[] };
export type CanvasPosition = {
  key: string;
  x: number;
  y: number;
  rotation: number;
  scale: number;
  z: number;
};
export type Organization = {
  revision: number;
  defaults: Defaults;
  pages: PersonalPage[];
  order: string[];
  canvas: CanvasPosition[];
};
export type Operation = {
  op: string;
  pageId?: string;
  cardKey?: string;
  beforeKey?: string | null;
  clientId?: string;
  title?: string;
  view?: string;
  group?: string;
  sort?: string;
  positions?: CanvasPosition[];
};
export type Group = { key: string; label: string; count: number; coverKeys: string[] };
export type CollectionResult = {
  items: PassportCard[];
  orderedKeys: string[];
  nextCursor: string | null;
  totalMatching: number;
  groupTotal: number;
  totals: { basePassports: number; earnedEditions: number };
  typeFacets: { kind: string; label: string; count: number }[];
  groups: Group[];
  queryVersion: string;
  datePolicy?: { basis: 'recorded_calendar'; version: 1 } | null;
  organization: Organization;
  showcase: PassportCard[];
  preferences: CardHub['preferences'];
};
export class CollectionError extends Error {
  code = '';
}
export async function collectionRequest<T>(query: URLSearchParams, body?: unknown): Promise<T> {
  const generation = api.sessionGeneration;
  const r = await api.makeRequest<T>(
    `/passport_collection.php?${query}`,
    body === undefined ? {} : { method: 'POST', body: JSON.stringify(body) }
  );
  if (generation !== api.sessionGeneration) throw new Error('Session changed');
  if (!r.success || !r.data) {
    const e = new CollectionError(r.error || 'Could not open your collection.');
    e.code = (r as { code?: string }).code || '';
    throw e;
  }
  return r.data;
}
export function moveKey(keys: string[], key: string, before: string | null): string[] {
  if (key === before) return keys;
  const next = keys.filter((k) => k !== key);
  const at = before === null ? next.length : next.indexOf(before);
  next.splice(at < 0 ? next.length : at, 0, key);
  return next;
}
export function applyOperation(state: Organization, op: Operation): Organization {
  const s = {
    ...state,
    pages: state.pages.map((p) => ({ ...p, keys: [...p.keys] })),
    order: [...state.order],
    canvas: state.canvas.map((p) => ({ ...p })),
  };
  if (op.op === 'create_page') s.pages.push({ id: op.clientId!, title: op.title!, keys: [] });
  else if (op.op === 'remove_page') s.pages = s.pages.filter((p) => p.id !== op.pageId);
  else if (op.op === 'rename_page') {
    const p = s.pages.find((p) => p.id === op.pageId);
    if (p) p.title = op.title!;
  } else if (op.op === 'set_defaults')
    s.defaults = { view: op.view as View, group: op.group!, sort: op.sort! };
  else if (op.op === 'set_canvas') s.canvas = op.positions ? op.positions.map((p) => ({ ...p })) : s.canvas;
  else {
    const p = s.pages.find((p) => p.id === op.pageId);
    const keys = op.pageId === 'all' ? s.order : p?.keys;
    if (keys) {
      const next =
        op.op === 'remove_from_page'
          ? keys.filter((k) => k !== op.cardKey)
          : moveKey(keys, op.cardKey!, op.beforeKey ?? null);
      if (op.pageId === 'all') s.order = next;
      else p!.keys = next;
    }
  }
  return s;
}
export function queryParams(params: URLSearchParams, defaults: Defaults): URLSearchParams {
  const p = new URLSearchParams();
  for (const key of ['q', 'types', 'edition', 'page', 'sort', 'group'])
    if (params.get(key)) p.set(key, params.get(key)!);
  if (!p.has('types') && params.get('type')) p.set('types', params.get('type')!);
  if (params.get('type') === 'edition') {
    p.delete('types');
    p.set('edition', 'earned');
  }
  if (!p.has('sort')) p.set('sort', defaults.sort);
  if (!p.has('group')) p.set('group', defaults.group);
  return p;
}
